import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Globe from 'apollo-react-icons/Globe';
import axiosInstance from '../../../../axiosInstance';
import { RESOURCES } from '../../../../constants/resourceConstants';

/**
 * Hook that fetches sidebar categories and returns the Categories menu item
 * in the format expected by the common Sidebar (menuKey, menuItems, etc.).
 * Returns an array of 0 or 1 item so it can be spread into menuItems.
 *
 * @param {Object} options
 * @param {Function} options.checkUserRole - (roleName) => boolean
 * @param {boolean} options.isProfileLoaded
 * @param {boolean} options.isSuper
 * @returns {{ categoriesMenuItems: Array }}
 */
export function useSidebarCategories({
  checkUserRole,
  isProfileLoaded,
  isSuper,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sidebarCategories, setSidebarCategories] = useState([]);

  const fetchSidebarCategories = useCallback(async () => {
    if (!isProfileLoaded) {
      return;
    }
    const canViewCategories = checkUserRole(
      RESOURCES.EXTERNAL_ORGANIZATIONS_CATEGORIES
    );

    if (!canViewCategories) {
      setSidebarCategories([]);
      return;
    }
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/api/categories/sidebar`,
        { withCredentials: true }
      );
      setSidebarCategories(response?.data || []);
    } catch (error) {
      console.error('Error fetching sidebar categories:', error);
      setSidebarCategories([]);
    }
  }, [checkUserRole, isProfileLoaded, isSuper]);

  useEffect(() => {
    fetchSidebarCategories();
  }, [fetchSidebarCategories]);

  useEffect(() => {
    const handleSidebarCategoriesUpdate = () => {
      fetchSidebarCategories();
    };

    window.addEventListener(
      'sidebarCategoriesUpdated',
      handleSidebarCategoriesUpdate
    );
    return () => {
      window.removeEventListener(
        'sidebarCategoriesUpdated',
        handleSidebarCategoriesUpdate
      );
    };
  }, [fetchSidebarCategories]);

  const canShowCategories =
    checkUserRole(RESOURCES.EXTERNAL_ORGANIZATIONS_CATEGORIES) &&
    sidebarCategories.length > 0;

  const categoriesMenuItems = canShowCategories && [
    {
      text: t('Categories'),
      icon: Globe,
      menuKey: 'categories',
      menuItems: sidebarCategories.map((category) => ({
        text: category.name,
        handleOnClick: () => {
          sessionStorage.setItem('sidebarCategoriesContext', 'true');
          navigate(`/admin/categories/${category.id}`, {
            state: { category, fromSidebarCategories: true },
          });
        },
        pathname: `/admin/categories/${category.id}`,
      })),
    },
  ];

  return {
    categoriesMenuItems: categoriesMenuItems || [],
  };
}
